// frontend/src/components/Sidebar.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Map,
  MapPinned,
  Search,
  Plus,
  Settings,
  LogOut,
  BarChart2,
  Building2,
  Bell,
  Briefcase,
  Rocket,
  ChevronDown,
  Home,
  ShieldCheck,
  BookOpen,
  Scale,
} from 'lucide-react';

import { logout } from '../services/authService';
import { useAuth } from '../context/AuthContext';
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../services/firebase';

// ✅ KPIs brutos (mesmos da Dashboard)
import {
  contarAnunciosAtivos,
  contarNegociosPorStatus,
  contarVisitasRealizadas,
  listarVendasPorMes,
} from '../services/dashboardService';

// ✅ Engine (para salvar KPI oficial no doc /score/{uid})
import { computeAndSaveScore } from '../services/leadershipEngine/scoreEngine';

// ✅ FONTE OFICIAL DO DOC score/{uid}
import { listenScoreSnapshot } from '../services/leadershipEngine/scoreRepository';

const AURORA = {
  emerald: '#34D399',
  cyan: '#22D3EE',
  purple: '#A78BFA',
  amber: '#F59E0B',
  text: 'rgba(17,24,39,0.92)',
  text2: 'rgba(17,24,39,0.62)',
  text3: 'rgba(17,24,39,0.42)',
};

function clamp(n, min, max) {
  const N = Number(n || 0);
  return Math.max(min, Math.min(max, N));
}

function toNumber(val, fallback = 0) {
  const n = Number(val);
  return Number.isFinite(n) ? n : fallback;
}

/* =========================================================
   MINI SCORE RADIAL — premium (Emerald -> Cyan -> Purple)
========================================================= */
function MiniScoreRadial({ value = 0, max = 6000, size = 56, stroke = 6 }) {
  const pct = useMemo(() => {
    const p = max > 0 ? value / max : 0;
    return clamp(p, 0, 1);
  }, [value, max]);

  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const C = 2 * Math.PI * r;

  const dash = C * pct;
  const gap = C - dash;

  const gid = useMemo(
    () => `mini_grad_${Math.random().toString(36).slice(2, 9)}`,
    []
  );

  const track = '#EEF2F7';

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        flex: '0 0 auto',
        display: 'inline-block',
      }}
      title={`Score: ${Math.round(value)} / ${max}`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.08))',
        }}
      >
        <defs>
          <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={AURORA.emerald} />
            <stop offset="50%" stopColor={AURORA.cyan} />
            <stop offset="100%" stopColor={AURORA.purple} />
          </linearGradient>
        </defs>

        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="transparent"
          stroke={track}
          strokeWidth={stroke}
        />

        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="transparent"
          stroke={`url(#${gid})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${gap}`}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ transition: 'stroke-dasharray 800ms ease' }}
        />
      </svg>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          userSelect: 'none',
          pointerEvents: 'none',
          lineHeight: 1,
        }}
      >
        <span
          style={{
            fontSize: Math.round(size * 0.28),
            fontWeight: 900,
            color: AURORA.text,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {Math.round(value)}
        </span>
      </div>
    </div>
  );
}

/* =========================================================
   AVATAR SIMPLES
========================================================= */
function Avatar({ photoUrl, initials = '?' }) {
  return (
    <div
      className="shrink-0 ring-2 ring-white shadow-sm"
      style={{
        width: 56,
        height: 56,
        borderRadius: 9999,
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
        background: '#2563eb',
      }}
    >
      {photoUrl ? (
        <img
          src={photoUrl}
          alt="Foto de perfil"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div className="w-full h-full text-white flex items-center justify-center font-bold uppercase">
          {initials}
        </div>
      )}
    </div>
  );
}

function stageLabel(pct) {
  if (pct <= 0.01) return 'Início';
  if (pct < 0.25) return 'Primeiros passos';
  if (pct < 0.5) return 'Em evolução';
  if (pct < 0.75) return 'Ritmo forte';
  if (pct < 0.9) return 'Alta performance';
  return 'Elite';
}

function PerformanceRow({ score = 0, max = 6000 }) {
  const pct = max > 0 ? clamp(score / max, 0, 1) : 0;
  const pctText = `${Math.round(pct * 100)}%`;

  return (
    <div className="w-full min-w-0">
      <div className="flex items-baseline justify-between gap-1">
        <div className="min-w-0">
          <p
            className="text-[12px] font-semibold truncate leading-none"
            style={{ color: AURORA.text }}
          >
            Performance
          </p>
          <p
            className="text-[10px] truncate mt-0.5"
            style={{ color: AURORA.text2 }}
          >
            {stageLabel(pct)}
          </p>
        </div>

        <div className="shrink-0 text-[11px]" style={{ color: AURORA.text2 }}>
          <span className="font-bold" style={{ color: AURORA.text }}>
            {pctText}
          </span>
        </div>
      </div>

      <div className="mt-1.5 h-[6px] w-full rounded-full overflow-hidden bg-gray-100">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${pct * 100}%`,
            background: `linear-gradient(90deg, ${AURORA.emerald}, ${AURORA.cyan}, ${AURORA.purple})`,
          }}
        />
      </div>
    </div>
  );
}

/* =========================================================
   SIDEBAR
========================================================= */
const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { usuario } = useAuth();

  // Contadores
  const [quantidadeImoveis, setQuantidadeImoveis] = useState(0);
  const [quantidadeSocios, setQuantidadeSocios] = useState(0);
  const [quantidadeParcerias, setQuantidadeParcerias] = useState(0);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState(0);

  // Menus
  const [cadastrarOpen, setCadastrarOpen] = useState(false);
  const [diretrizesOpen, setDiretrizesOpen] = useState(false);

  // ✅ KPI Score (4 pilares) — fallback local
  const [kpiScore, setKpiScore] = useState(0);

  // ✅ Depreciação e max do doc /score/{uid}
  const [depreciacaoPts, setDepreciacaoPts] = useState(0);

  // ✅ IMPORTANTE: ScoreMax agora default 6000 (igual dashboard)
  const [scoreMax, setScoreMax] = useState(6000);

  // ✅ Score oficial salvo no doc (prioridade)
  const [scoreOficial, setScoreOficial] = useState(null);

  // ✅ FINAL: prioriza scoreOficial; fallback = KPI
  const scoreFinal = clamp(scoreOficial ?? kpiScore, 0, scoreMax);

  // 1) Contadores de perfil
  useEffect(() => {
    if (!usuario?.uid) return;

    const contarRelacionamentos = async () => {
      try {
        const STATUS_ACEITO = 'accepted';

        const q1 = query(
          collection(db, 'socios'),
          where('status', '==', STATUS_ACEITO),
          where('criador', '==', usuario.uid)
        );

        const q2 = query(
          collection(db, 'socios'),
          where('status', '==', STATUS_ACEITO),
          where('convidado', '==', usuario.uid)
        );

        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

        const socios = [...snap1.docs, ...snap2.docs].map((d) => {
          const data = d.data();
          return data.criador === usuario.uid ? data.convidado : data.criador;
        });

        setQuantidadeSocios(socios.length);

        const imoveisRef = collection(db, 'imoveis');

        const meusImoveisSnap = await getDocs(
          query(imoveisRef, where('criadoPor.uid', '==', usuario.uid))
        );

        let imoveisCount = meusImoveisSnap.size;

        for (const socioUid of socios) {
          const imoveisSocioSnap = await getDocs(
            query(
              imoveisRef,
              where('criadoPor.uid', '==', socioUid),
              where('compartilharComSocios', '==', true)
            )
          );

          imoveisCount += imoveisSocioSnap.size;
        }

        setQuantidadeImoveis(imoveisCount);

        const qParcerias = query(
          collection(db, 'parcerias'),
          where('status', '==', STATUS_ACEITO),
          where('usuario', '==', usuario.uid)
        );

        const snapParcerias = await getDocs(qParcerias);
        setQuantidadeParcerias(snapParcerias.size);
      } catch (error) {
        console.error('Erro ao contar dados:', error);
      }
    };

    contarRelacionamentos();
  }, [usuario?.uid]);

  // 2) Notificações
  useEffect(() => {
    if (!usuario?.uid) return;

    const q = query(
      collection(db, 'notificacoes'),
      where('destinatarioUid', '==', usuario.uid),
      where('lida', '==', false)
    );

    const unsub = onSnapshot(q, (snap) => setNotificacoesNaoLidas(snap.size));

    return () => unsub();
  }, [usuario?.uid]);

  // 3) Listener do doc score/{uid} — fonte oficial
  useEffect(() => {
    if (!usuario?.uid) return;

    const unsub = listenScoreSnapshot(
      usuario.uid,
      (data) => {
        const max = toNumber(data?.maxTotal, 6000) || 6000;
        const dep = toNumber(data?.depreciacao?.total, 0);

        const total = toNumber(
          data?.scoreTotal ??
            data?.totalFinal ??
            data?.kpiTotalFinal ??
            data?.total ??
            0,
          0
        );

        setScoreMax(max);
        setDepreciacaoPts(dep);
        setScoreOficial(total);
      },
      (err) => console.warn('[Sidebar] listenScoreSnapshot erro:', err)
    );

    return () => unsub?.();
  }, [usuario?.uid]);

  // 4) KPIs brutos -> KPI Score (fallback) + salva KPI no doc /score/{uid}
  useEffect(() => {
    if (!usuario?.uid) return;

    let ativo = true;

    const calcularKPIsAoVivo = async () => {
      try {
        const [anunciosAtivos, negociosPorStatus, visitas, vendasMensal] =
          await Promise.all([
            contarAnunciosAtivos(usuario.uid),
            contarNegociosPorStatus(usuario.uid),
            contarVisitasRealizadas(usuario.uid),
            listarVendasPorMes(usuario.uid, { meses: 12 }),
          ]);

        if (!ativo) return;

        const totalNegocios = Object.values(negociosPorStatus || {}).reduce(
          (acc, curr) => acc + (Number(curr) || 0),
          0
        );

        const totalContratos = Array.isArray(vendasMensal)
          ? vendasMensal.reduce(
              (acc, m) => acc + (Number(m.quantidade) || 0),
              0
            )
          : 0;

        const targetAng = 40;
        const angariacaoMax = 300;

        const targetNeg = 30;
        const negociosMax = 200;

        const targetVis = 20;
        const visitasMax = 200;

        const targetFec = 12;
        const fechamentosMax = 300;

        const angPts = clamp(
          (toNumber(anunciosAtivos) / targetAng) * angariacaoMax,
          0,
          angariacaoMax
        );

        const negPts = clamp(
          (toNumber(totalNegocios) / targetNeg) * negociosMax,
          0,
          negociosMax
        );

        const visPts = clamp(
          (toNumber(visitas) / targetVis) * visitasMax,
          0,
          visitasMax
        );

        const fecPts = clamp(
          (toNumber(totalContratos) / targetFec) * fechamentosMax,
          0,
          fechamentosMax
        );

        const totalKpi = angPts + negPts + visPts + fecPts;

        setKpiScore(totalKpi);

        computeAndSaveScore(usuario.uid, {
          angariacaoPts: angPts,
          negociosPts: negPts,
          visitasPts: visPts,
          fechamentosPts: fecPts,
          angariacaoMax,
          negociosMax,
          visitasMax,
          fechamentosMax,
          total: totalKpi,
        }).catch((e) =>
          console.warn('[Sidebar] computeAndSaveScore KPI (seguindo):', e)
        );
      } catch (err) {
        console.error('Erro ao calcular KPI score sidebar:', err);
      }
    };

    calcularKPIsAoVivo();

    return () => {
      ativo = false;
    };
  }, [usuario?.uid]);

  // Auto-expande Menu Cadastrar
  useEffect(() => {
    const isCadastro =
      location.pathname === '/cadastrar' ||
      location.pathname.startsWith('/cadastrar') ||
      location.pathname === '/cadastrar-incorporadora' ||
      location.pathname.startsWith('/cadastrar-incorporadora');

    if (isCadastro) setCadastrarOpen(true);
  }, [location.pathname]);

  // Auto-expande Menu Diretrizes da Rede
  useEffect(() => {
    const isDiretrizes = location.pathname.startsWith('/diretrizes-rede');

    if (isDiretrizes) setDiretrizesOpen(true);
  }, [location.pathname]);

  const links = [
    { to: '/perfil', icon: <Home size={18} />, label: 'Perfil' },
    { to: '/', icon: <BarChart2 size={18} />, label: 'Dashboard' },
    {
      to: '/negocios/meus',
      icon: <Briefcase size={18} />,
      label: 'Central de Negócios',
    },
    {
      to: '/maquina-de-vendas',
      icon: <Rocket size={18} />,
      label: 'Máquina de Vendas',
    },
    {
      to: '/home',
      icon: <Map size={18} />,
      label: 'Explorar Imóveis',
    },
    {
      to: '/ponte-imobiliaria',
      icon: <MapPinned size={18} />,
      label: 'Ponte Imobiliária',
    },
    {
      to: '/notificacoes',
      icon: <Bell size={18} />,
      label: 'Notificações',
      badge: notificacoesNaoLidas,
    },
    {
      to: '/buscar-socios',
      icon: <Search size={18} />,
      label: 'Buscar Sócios',
    },
    {
      type: 'cadastrar',
    },
    {
      type: 'diretrizes',
    },
    {
      to: '/configuracoes',
      icon: <Settings size={18} />,
      label: 'Configurações',
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  const formatarEspecialidade = (valor) => {
    switch (valor) {
      case 'alto_padrao':
        return 'Alto padrão';
      case 'comercial':
        return 'Comercial';
      case 'residencial':
        return 'Residencial';
      case 'rural':
        return 'Rural';
      case 'empreendimentos':
        return 'Empreendimentos';
      default:
        return 'Especialidade não informada';
    }
  };

  const isActiveRoute = (to) =>
    location.pathname === to ||
    (to !== '/' && location.pathname.startsWith(to));

  const cadastrarActive =
    location.pathname === '/cadastrar' ||
    location.pathname.startsWith('/cadastrar') ||
    location.pathname === '/cadastrar-incorporadora' ||
    location.pathname.startsWith('/cadastrar-incorporadora');

  const diretrizesActive = location.pathname.startsWith('/diretrizes-rede');

  const initials =
    usuario?.nome
      ?.split(' ')
      .map((n) => n?.[0])
      .join('')
      .slice(0, 2) || '?';

  return (
    <div className="h-screen w-64 bg-white border-r border-gray-200 text-gray-900 flex flex-col justify-between">
      <div className="p-4 overflow-y-auto sidebar-scroll">
        {/* Logo */}
        <div className="mb-4 flex items-center gap-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 leading-none">
              Elion
            </h1>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">
              Seu parceiro
            </p>
          </div>
        </div>

        {/* Card do usuário */}
        <div className="border border-gray-200 rounded-lg p-3 mb-6 bg-gray-50/50">
          <div className="flex gap-3">
            <div className="flex flex-col items-center gap-3 shrink-0">
              <Avatar
                photoUrl={usuario?.fotoPerfil?.url || ''}
                initials={initials}
              />

              <MiniScoreRadial
                value={scoreFinal}
                max={scoreMax}
                size={56}
                stroke={6}
              />
            </div>

            <div className="flex flex-col justify-between min-w-0 flex-1 py-0.5">
              <div className="flex flex-col">
                <p className="font-bold text-sm text-gray-900 truncate leading-tight">
                  {usuario?.nome || 'Usuário'}
                </p>

                <p className="text-xs text-gray-600 truncate mt-0.5">
                  {formatarEspecialidade(usuario?.especialidade)}
                </p>

                <p className="text-[10px] text-gray-400 mt-1 truncate">
                  {quantidadeImoveis} imóveis • {quantidadeSocios} sócios •{' '}
                  {quantidadeParcerias} parcerias
                </p>
              </div>

              <div className="mt-auto pt-2">
                <PerformanceRow score={scoreFinal} max={scoreMax} />
              </div>
            </div>
          </div>
        </div>

        {/* Links */}
        <nav className="space-y-1">
          {links.map((item, idx) => {
            if (item.type === 'cadastrar') {
              return (
                <div key={`cadastrar-${idx}`} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => setCadastrarOpen((v) => !v)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                      cadastrarActive
                        ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-700 font-medium'
                        : 'hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <Plus size={18} />
                      Cadastrar
                    </span>

                    <ChevronDown
                      size={18}
                      className={`transition-transform ${
                        cadastrarOpen ? 'rotate-180' : 'rotate-0'
                      }`}
                    />
                  </button>

                  {cadastrarOpen && (
                    <div className="ml-2 pl-3 border-l border-gray-200 space-y-1">
                      <Link
                        to="/cadastrar"
                        className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                          isActiveRoute('/cadastrar')
                            ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-700 font-medium'
                            : 'hover:bg-gray-100 text-gray-800'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Home size={18} />
                          Cadastrar Imóveis
                        </span>
                      </Link>

                      <Link
                        to="/cadastrar-incorporadora"
                        className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                          isActiveRoute('/cadastrar-incorporadora')
                            ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-700 font-medium'
                            : 'hover:bg-gray-100 text-gray-800'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Building2 size={18} />
                          Cadastrar Incorporadora
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
              );
            }

            if (item.type === 'diretrizes') {
              return (
                <div key={`diretrizes-${idx}`} className="space-y-1">
                  <button
                    type="button"
                    onClick={() => setDiretrizesOpen((v) => !v)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                      diretrizesActive
                        ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-700 font-medium'
                        : 'hover:bg-gray-100 text-gray-800'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <ShieldCheck size={18} />
                      Diretrizes da Rede
                    </span>

                    <ChevronDown
                      size={18}
                      className={`transition-transform ${
                        diretrizesOpen ? 'rotate-180' : 'rotate-0'
                      }`}
                    />
                  </button>

                  {diretrizesOpen && (
                    <div className="ml-2 pl-3 border-l border-gray-200 space-y-1">
                      <Link
                        to="/diretrizes-rede/manifesto"
                        className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                          location.pathname === '/diretrizes-rede/manifesto'
                            ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-700 font-medium'
                            : 'hover:bg-gray-100 text-gray-800'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <BookOpen size={18} />
                          Manifesto e Cultura
                        </span>
                      </Link>

                      <Link
                        to="/diretrizes-rede/modelo-operacional"
                        className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                          location.pathname ===
                          '/diretrizes-rede/modelo-operacional'
                            ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-700 font-medium'
                            : 'hover:bg-gray-100 text-gray-800'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Briefcase size={18} />
                          Modelo Operacional
                        </span>
                      </Link>

                      <Link
                        to="/diretrizes-rede/compliance"
                        className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                          location.pathname === '/diretrizes-rede/compliance'
                            ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-700 font-medium'
                            : 'hover:bg-gray-100 text-gray-800'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Scale size={18} />
                          Termos e Compliance
                        </span>
                      </Link>
                    </div>
                  )}
                </div>
              );
            }

            const { to, icon, label, badge } = item;
            const active = isActiveRoute(to);

            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${
                  active
                    ? 'bg-blue-50 border-l-2 border-blue-600 text-blue-700 font-medium'
                    : 'hover:bg-gray-100 text-gray-800'
                }`}
              >
                <span className="flex items-center gap-2">
                  {icon}
                  {label}
                </span>

                {typeof badge === 'number' && badge > 0 && (
                  <span className="min-w-5 h-5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center px-1">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Rodapé */}
      <footer className="p-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition w-full"
        >
          <LogOut size={16} />
          Sair
        </button>

        <p className="text-xs text-center text-gray-400 mt-3">
          © {new Date().getFullYear()} Elion
        </p>
      </footer>
    </div>
  );
};

export default Sidebar;
